import React, { useCallback, useEffect, useMemo, useState } from "react";
import Navbar from "../components/Navbar";
import BandCard, { BandRow } from "../components/BandCard";
import api from "../lib/api";
import { useLang } from "../i18n";
import { Search, Plus, Skull, LayoutGrid, List, SlidersHorizontal, Eye } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { regionName, flagEmoji, codeFromName } from "../lib/countries";

const PAGE_SIZE = 24;

export default function Dashboard() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [bands, setBands] = useState([]);
  const [category, setCategory] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(() => localStorage.getItem("mv_view") || "grid");
  const [sort, setSort] = useState(() => localStorage.getItem("mv_sort") || "date_desc");
  const [showFilters, setShowFilters] = useState(false);
  const [fGenre, setFGenre] = useState("");
  const [fCountry, setFCountry] = useState("");
  const [fRating, setFRating] = useState(0);
  const [fSeen, setFSeen] = useState(false);
  const [page, setPage] = useState(1);

  const setViewMode = (m) => { setView(m); localStorage.setItem("mv_view", m); };
  const setSortMode = (s) => { setSort(s); localStorage.setItem("mv_sort", s); };

  const countryList = useMemo(() => {
    const codes = {};
    bands.forEach((b) => {
      const code = ((b.country_code || "") || codeFromName(b.country, lang)).toUpperCase();
      if (code) codes[code] = true;
    });
    return Object.keys(codes)
      .map((code) => ({ code, name: regionName(code, lang), flag: flagEmoji(code) }))
      .sort((a, b) => a.name.localeCompare(b.name, lang));
  }, [bands, lang]);

  const filteredBands = useMemo(() => {
    const g = fGenre.trim().toLowerCase();
    return bands.filter((b) => {
      if (g && !((b.genre || "").toLowerCase().includes(g) || (b.subgenre || "").toLowerCase().includes(g))) return false;
      if (fCountry) {
        const code = ((b.country_code || "") || codeFromName(b.country, lang)).toUpperCase();
        if (code !== fCountry) return false;
      }
      if (fRating && (b.rating || 0) < fRating) return false;
      if (fSeen && !(b.seen_count > 0)) return false;
      return true;
    });
  }, [bands, fGenre, fCountry, fRating, fSeen, lang]);

  const sortedBands = useMemo(() => [...filteredBands].sort((a, b) => {
    switch (sort) {
      case "name_asc": return a.name.localeCompare(b.name);
      case "name_desc": return b.name.localeCompare(a.name);
      case "rating_desc": return (b.rating || 0) - (a.rating || 0);
      case "rating_asc": return (a.rating || 0) - (b.rating || 0);
      case "date_asc": return (a.created_at || "").localeCompare(b.created_at || "");
      default: return (b.created_at || "").localeCompare(a.created_at || "");
    }
  }), [filteredBands, sort]);

  const totalPages = Math.max(1, Math.ceil(sortedBands.length / PAGE_SIZE));
  const pageClamped = Math.min(page, totalPages);
  const pagedBands = sortedBands.slice((pageClamped - 1) * PAGE_SIZE, pageClamped * PAGE_SIZE);

  useEffect(() => { setPage(1); }, [fGenre, fCountry, fRating, fSeen, sort, category, search]);
  const activeFilters = (fGenre ? 1 : 0) + (fCountry ? 1 : 0) + (fRating ? 1 : 0) + (fSeen ? 1 : 0);
  const clearFilters = () => { setFGenre(""); setFCountry(""); setFRating(0); setFSeen(false); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (category) params.category = category;
      if (search) params.search = search;
      const { data } = await api.get("/bands", { params });
      setBands(data);
    } finally { setLoading(false); }
  }, [category, search]);

  useEffect(() => {
    const tm = setTimeout(load, 300);
    return () => clearTimeout(tm);
  }, [load]);

  const tabs = [
    { key: "", label: t("all") },
    { key: "Metal", label: t("metal") },
    { key: "Rock", label: t("rock") },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="heading text-4xl font-bold text-white">{t("directory")}</h1>
            <p className="text-zinc-500 text-sm mt-1">{bands.length} {t("bands_count")}</p>
          </div>
          <div className="relative w-full md:w-72">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              data-testid="search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("search_band")}
              className="w-full bg-black border border-zinc-800 rounded-sm pl-10 pr-3 py-2.5 text-white focus:border-primary focus:outline-none"
            />
          </div>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-8 border-b border-zinc-800">
          <div className="flex items-center gap-2 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                data-testid={`tab-${tab.key || "all"}`}
                onClick={() => setCategory(tab.key)}
                className={`px-4 py-3 label-caps text-sm border-b-2 -mb-px transition-colors whitespace-nowrap ${
                  category === tab.key ? "border-primary text-primary" : "border-transparent text-zinc-400 hover:text-white"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 pb-2 flex-wrap">
            <button
              data-testid="toggle-filters-btn"
              onClick={() => setShowFilters((s) => !s)}
              title={t("advanced_filters")}
              className={`relative p-2 rounded-sm transition-colors ${showFilters || activeFilters ? "bg-zinc-800 text-primary" : "text-zinc-500 hover:text-white"}`}
            >
              <SlidersHorizontal size={18} />
              {activeFilters > 0 && <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">{activeFilters}</span>}
            </button>
            <select
              data-testid="sort-select"
              value={sort}
              onChange={(e) => setSortMode(e.target.value)}
              className="bg-black border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-200 focus:border-primary focus:outline-none"
            >
              <option value="date_desc">{t("sort_recent")}</option>
              <option value="date_asc">{t("sort_oldest")}</option>
              <option value="name_asc">{t("sort_az")}</option>
              <option value="name_desc">{t("sort_za")}</option>
              <option value="rating_desc">{t("sort_rating_desc")}</option>
              <option value="rating_asc">{t("sort_rating_asc")}</option>
            </select>
            <button
              data-testid="view-grid-btn"
              onClick={() => setViewMode("grid")}
              title={t("grid_view")}
              className={`p-2 rounded-sm transition-colors ${view === "grid" ? "bg-zinc-800 text-primary" : "text-zinc-500 hover:text-white"}`}
            >
              <LayoutGrid size={18} />
            </button>
            <button
              data-testid="view-list-btn"
              onClick={() => setViewMode("list")}
              title={t("list_view")}
              className={`p-2 rounded-sm transition-colors ${view === "list" ? "bg-zinc-800 text-primary" : "text-zinc-500 hover:text-white"}`}
            >
              <List size={18} />
            </button>
          </div>
        </div>

        {showFilters && (
          <div data-testid="advanced-filters" className="flex flex-col sm:flex-row sm:items-end gap-3 mb-8 bg-zinc-900 border border-zinc-800 rounded-sm p-4">
            <div className="flex-1">
              <label className="label-caps text-xs text-zinc-500">{t("filter_genre")}</label>
              <input data-testid="filter-genre-input" value={fGenre} onChange={(e) => setFGenre(e.target.value)} placeholder="ex: thrash, doom..."
                className="w-full bg-black border border-zinc-800 rounded-sm px-3 py-2 text-white text-sm focus:border-primary focus:outline-none" />
            </div>
            <div className="flex-1">
              <label className="label-caps text-xs text-zinc-500">{t("filter_country")}</label>
              <select data-testid="filter-country-select" value={fCountry} onChange={(e) => setFCountry(e.target.value)}
                className="w-full bg-black border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-200 focus:border-primary focus:outline-none">
                <option value="">{t("all_countries")}</option>
                {countryList.map((c) => <option key={c.code} value={c.code}>{c.flag} {c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label-caps text-xs text-zinc-500">{t("filter_rating")}</label>
              <select data-testid="filter-rating-select" value={fRating} onChange={(e) => setFRating(Number(e.target.value))}
                className="w-full bg-black border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-200 focus:border-primary focus:outline-none">
                <option value={0}>—</option>
                <option value={1}>★ 1+</option>
                <option value={2}>★ 2+</option>
                <option value={3}>★ 3</option>
              </select>
            </div>
            <button type="button" data-testid="filter-seen-toggle" onClick={() => setFSeen((v) => !v)}
              className={`flex items-center gap-2 px-4 py-2 label-caps text-sm rounded-sm border transition-colors ${fSeen ? "border-green-500 text-green-400 bg-green-900/20" : "border-zinc-700 text-zinc-300 hover:border-primary hover:text-primary"}`}>
              <Eye size={16} /> {t("filter_seen_only")}
            </button>
            {activeFilters > 0 && (
              <button data-testid="clear-filters-btn" onClick={clearFilters}
                className="px-4 py-2 border border-zinc-700 text-zinc-300 label-caps text-sm rounded-sm hover:border-primary hover:text-primary">
                {t("clear_filters")}
              </button>
            )}
          </div>
        )}

        {loading ? (
          <p className="text-zinc-500">...</p>
        ) : bands.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <Skull size={56} className="text-zinc-700 mb-4" />
            <p className="text-zinc-400 max-w-sm">{t("no_bands")}</p>
            <button data-testid="empty-add-band" onClick={() => navigate("/bands/new")}
              className="mt-6 flex items-center gap-2 px-5 py-3 bg-primary text-primary-foreground label-caps rounded-sm font-semibold hover:bg-yellow-400 transition-colors">
              <Plus size={18} /> {t("add_band")}
            </button>
          </div>
        ) : sortedBands.length === 0 ? (
          <p data-testid="no-results" className="text-zinc-500 text-center py-16">{t("no_results")}</p>
        ) : (
          <>
            {view === "grid" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {pagedBands.map((b) => <BandCard key={b.id} band={b} />)}
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {pagedBands.map((b) => <BandRow key={b.id} band={b} />)}
              </div>
            )}
            {totalPages > 1 && (
              <div data-testid="pagination" className="flex items-center justify-center gap-4 mt-10">
                <button data-testid="prev-page-btn" disabled={pageClamped <= 1} onClick={() => setPage(pageClamped - 1)}
                  className="px-4 py-2 border border-zinc-700 text-zinc-300 label-caps text-sm rounded-sm hover:border-primary hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed">
                  {t("prev_page")}
                </button>
                <span className="text-sm text-zinc-400">{t("page_label")} {pageClamped} / {totalPages}</span>
                <button data-testid="next-page-btn" disabled={pageClamped >= totalPages} onClick={() => setPage(pageClamped + 1)}
                  className="px-4 py-2 border border-zinc-700 text-zinc-300 label-caps text-sm rounded-sm hover:border-primary hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed">
                  {t("next_page")}
                </button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
